import { useCallback, useEffect, useRef, useState } from "react";

export interface UseAppUpdateOptions {
  currentVersion: string | undefined;
  versionUrl?: string;
  intervalMs?: number;
  onApply: () => void | Promise<void>;
}

export interface UseAppUpdateResult {
  updateAvailable: boolean;
  latestVersion: string | undefined;
  apply: () => void;
  dismiss: () => void;
}

export function useAppUpdate({
  currentVersion,
  versionUrl = "/version.json",
  intervalMs = 60_000,
  onApply,
}: UseAppUpdateOptions): UseAppUpdateResult {
  const [latestVersion, setLatestVersion] = useState<string | undefined>(undefined);
  const [dismissedVersion, setDismissedVersion] = useState<string | undefined>(undefined);
  // Inert in local dev: an unset or "dev" currentVersion has no meaningful
  // "newer" build to detect, so there is nothing useful to poll for.
  const inert = !currentVersion || currentVersion === "dev";

  const check = useCallback(async () => {
    if (inert) return;
    try {
      const res = await fetch(versionUrl, { cache: "no-store" });
      if (!res.ok) return;
      const data = (await res.json()) as { version?: unknown };
      if (typeof data.version === "string" && data.version.length > 0) {
        setLatestVersion(data.version);
      }
    } catch {
      // Offline or unreachable — normal, nothing to surface.
    }
  }, [inert, versionUrl]);

  const checkRef = useRef(check);
  checkRef.current = check;

  useEffect(() => {
    if (inert) return;
    checkRef.current();
    const interval = setInterval(() => checkRef.current(), intervalMs);
    const onVisible = () => {
      if (document.visibilityState === "visible") checkRef.current();
    };
    const onOnline = () => checkRef.current();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", onOnline);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", onOnline);
    };
  }, [inert, intervalMs]);

  const updateAvailable =
    !inert &&
    latestVersion !== undefined &&
    latestVersion !== currentVersion &&
    latestVersion !== dismissedVersion;

  const apply = useCallback(() => {
    onApply();
  }, [onApply]);

  const dismiss = useCallback(() => {
    setDismissedVersion(latestVersion);
  }, [latestVersion]);

  return { updateAvailable, latestVersion, apply, dismiss };
}
